import Modal from '@components/molecules/modal';
import Checkbox from '@components/atoms/inputs/checkbox';
import globalModalAtom, {
    ConfirmModalProps,
    InfoModalProps,
} from '@stateAtoms/globalModalAtom';
import { store } from '@stateAtoms/store';
import { useAtom, useAtomValue } from 'jotai';
import { Button } from '@minds/minds-react-sdk';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function GlobalModalProvider() {
    const [modal, setModal] = useAtom(globalModalAtom, { store });

    if (!modal) return null;

    function getType() {
        if (modal?.type === 'confirm') return ConfirmModal;
        if (modal?.type === 'info') return InfoModal;
        return Fragment;
    }

    const ModalComponent = getType();

    return (
        <Modal
            title={modal.title}
            open
            onClose={() => { modal.onCancel?.(); setModal(null) }}
            backdropClassName={modal.backdropClassName ?? 'z-50'}
        >
            <ModalComponent onClose={() => { modal.onCancel?.(); setModal(null) }} onDismiss={() => setModal(null)} />
        </Modal>
    );
}

function ConfirmModal({ onClose, onDismiss }: { onClose: () => void; onDismiss: () => void }) {
    const modal = useAtomValue(globalModalAtom, { store }) as ConfirmModalProps;
    const { t } = useTranslation();
    const [isConfirming, setIsConfirming] = useState(false);
    const [rememberChoice, setRememberChoice] = useState(
        modal.checkboxDefaultChecked ?? false,
    );

    async function handleConfirm() {
        setIsConfirming(true);
        try {
            await modal.onConfirm(rememberChoice);
        } finally {
            setIsConfirming(false);
            onDismiss();
        }
    }

    return (
        <>
            <Modal.Content>
                <p>{modal.message}</p>
                {modal.checkboxLabel ? (
                    <Checkbox
                        id="global-modal-checkbox"
                        label={modal.checkboxLabel}
                        checked={rememberChoice}
                        onChange={(event) =>
                            setRememberChoice(event.target.checked)
                        }
                        wrapperClassName="mt-4"
                    />
                ) : null}
            </Modal.Content>

            <Modal.Footer>
                <Button
                    variant="outline-dark"
                    className="w-48"
                    content={modal.cancelText ?? t('cancel')}
                    onClick={onClose}
                    data-testid='cancel-modal-button'
                />
                <Button
                    onClick={handleConfirm}
                    loading={isConfirming}
                    content={modal.confirmText ?? t('confirm')}
                    className="w-48"
                    data-testid='confirm-modal-button'
                />
            </Modal.Footer>
        </>
    );
}

function InfoModal({ onClose }: { onClose: () => void }) {
    const modal = useAtomValue(globalModalAtom, { store }) as InfoModalProps;
    const { t } = useTranslation();

    return (
        <>
            <Modal.Content>
                <p>{modal.message}</p>
            </Modal.Content>

            <Modal.Footer>
                <Button
                    onClick={onClose}
                    content={modal.buttonText ?? t('ok')}
                    className="w-48"
                    data-testid='close-modal-button'
                />
            </Modal.Footer>
        </>
    );
}
